import {
  EffectComposer,
  Glitch,
  ChromaticAberration,
  Vignette,
} from '@react-three/postprocessing';

import { useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useEffect, useRef, useState } from 'react';

import './klein.css';
import { Mesh, Vector2 } from 'three';
import type { JSX } from 'react/jsx-runtime';

const X_OFFSET = 0.01;
const MIN_Y_OFFSET = 0.01;
const MAX_Y_OFFSET = 0.05;
const HOVER_Y_OFFSET = 0.025;
const MAX_SCROLL_SPEED = 4000;

/** idle spin, rad/s - matches the 0.005 rad per 16ms tick this replaced */
const BASE_SPIN = 0.32;
/** extra rad/s while the bottle is hovered */
const HOVER_SPIN = 2.4;
/** extra rad/s at MAX_SCROLL_SPEED, decaying once the scroll stops */
const SCROLL_SPIN = 6;
/** per-frame (at 60fps) decay of the scroll boost */
const SPIN_DECAY = 0.94;
/** stable identity, so r3f applies it once and never fights the useFrame spin */
const INITIAL_ROTATION: [number, number, number] = [-Math.PI * 0.5, 0, 0];

function PostProcessing({
  glitchActive,
  hoverActive,
  offsetY,
  dir,
}: {
  glitchActive: boolean;
  hoverActive: boolean;
  offsetY: number;
  dir: 1 | -1;
}) {
  const { matches } = window.matchMedia('(prefers-reduced-motion: reduce)');
  return (
    <EffectComposer>
      {hoverActive && !matches ? (
        <Glitch
          strength={new Vector2(0, 0.05)}
          duration={new Vector2(0.5, 0.7)}
          delay={new Vector2(0, 0.1)}
          mode={3}
        />
      ) : (
        <></>
      )}
      {(glitchActive || hoverActive) && !matches ? (
        <>
          <ChromaticAberration
            offset={[X_OFFSET, (hoverActive ? HOVER_Y_OFFSET : offsetY) * dir]}
          />
        </>
      ) : (
        <></>
      )}
      <Vignette eskil={false} offset={0.1} darkness={1.2} />
    </EffectComposer>
  );
}

export function Model(props: JSX.IntrinsicElements['group']) {
  const { nodes } = useGLTF('/klein_bottle-transformed.glb');
  const sphere = nodes.Sphere001__0 as Mesh;

  const meshRef = useRef<Mesh>(null);
  /** decaying angular velocity added by the last scroll */
  const scrollSpin = useRef(0);
  const reduced = useRef(false);
  const [glitchEnabled, setGlitch] = useState(false);
  const [hoverActive, setHover] = useState(false);
  const [offsetY, setOffsetY] = useState<number>(MIN_Y_OFFSET);
  const [dir, setDir] = useState<1 | -1>(1);

  const prevScrollY = useRef<number>(
    typeof window !== 'undefined' ? window.scrollY : 0
  );
  const prevTime = useRef<number | null>(null);

  const scrollcb = () => {
    const now = performance.now();
    const currentY = window.scrollY;
    if (prevTime.current !== null) {
      const dyRaw = currentY - prevScrollY.current;
      const dy = Math.abs(dyRaw);
      const dt = Math.max(1, now - prevTime.current); // ms
      const speedPxPerSec = (dy / dt) * 1000; // px/s
      const t = Math.min(1, Math.max(0, speedPxPerSec / MAX_SCROLL_SPEED));
      const newOffsetY = MIN_Y_OFFSET + t * (MAX_Y_OFFSET - MIN_Y_OFFSET);
      setOffsetY(newOffsetY);
      // take the fastest recent scroll rather than the latest, so a burst of
      // events doesn't average the kick away
      if (!reduced.current) {
        scrollSpin.current = Math.max(scrollSpin.current, t * SCROLL_SPIN);
      }
      if (dyRaw !== 0) setDir(dyRaw > 0 ? 1 : -1);
    }
    prevScrollY.current = currentY;
    prevTime.current = now;

    setGlitch(true);
    setTimeout(() => setGlitch(false), 500);
  };
  useEffect(() => {
    window.addEventListener('scroll', scrollcb);
    prevTime.current = performance.now();
    prevScrollY.current = window.scrollY;
    return () => window.removeEventListener('scroll', scrollcb);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => (reduced.current = mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // spun on the render loop rather than a setInterval pushing state: the speed
  // can vary, and the component no longer re-renders 60 times a second
  useFrame((_, delta) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const dt = Math.min(delta, 0.1); // a backgrounded tab shouldn't lurch
    const boost = reduced.current
      ? 0
      : scrollSpin.current + (hoverActive ? HOVER_SPIN : 0);
    mesh.rotation.z += (BASE_SPIN + boost) * dt;
    scrollSpin.current *= Math.pow(SPIN_DECAY, dt * 60);
  });

  return (
    <group {...props} dispose={null}>
      <mesh
        ref={meshRef}
        geometry={sphere.geometry}
        rotation={INITIAL_ROTATION}
        onPointerOver={(e) => setHover(true)}
        onPointerOut={(e) => setHover(false)}
      >
        <meshStandardMaterial wireframe color="#fff" />
      </mesh>
      <PostProcessing
        glitchActive={glitchEnabled}
        hoverActive={hoverActive}
        offsetY={offsetY}
        dir={dir}
      />
    </group>
  );
}

useGLTF.preload('/klein_bottle-transformed.glb');
